import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileCheck2 } from "lucide-react";
import {
  ApiError,
  createConsentWithdrawal,
  getMyConsents,
  reaffirmMyConsents,
} from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import type {
  InviteConsents,
  MyConsentRecord,
  PendingConsentAffirmation,
} from "@/lib/types";
import { Button } from "@/components/ui/Button";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import {
  ConsentPanel,
  EMPTY_CONSENTS,
  requiredConsentsMet,
} from "@/features/onboarding/components/ConsentPanel";

/**
 * The `/me?tab=consent` surface — the parent's own consent record
 * (docs/plans/consent-visibility-plan.md piece 1) plus the re-consent
 * and withdrawal-request controls (piece 4).
 *
 * PRIVACY (docs/PRIVACY.md, asserted by tests/e2e/consent-visibility.spec.js
 * — written before this renderer): everything shown here comes from an
 * explicit allowlist of fields on the caller's OWN rows. The operator's
 * `note` / `recorded_by_user_id` are never templated even if a payload
 * carries them, and a co-guardian's affirmation for the same child is
 * server-side invisible to this endpoint.
 */

/** Plain-language names for the stored documents and per-child items —
 * raw payload keys must never reach the DOM. */
const DOC_LABELS: Record<string, string> = {
  "beta-participation-agreement": "Beta Participation Agreement",
  "beta-privacy-notice": "Beta Privacy Notice",
  "parental-consent": "Parental consent",
};

const ITEM_LABELS: Record<string, string> = {
  roster: "Roster entry",
  video: "Match & training video",
  feedback: "Coaching feedback",
  photo: "Profile photo (optional)",
};

function formatDate(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function ConsentTab() {
  const query = useQuery({
    queryKey: unscopedKey("me-consents"),
    queryFn: getMyConsents,
  });

  if (query.isLoading) {
    return (
      <div data-testid="me-consent-tab" className="text-sm text-ink-muted">
        Loading your consent record…
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div data-testid="me-consent-tab" className="text-sm text-ink-muted">
        Couldn't load your consent record. Try again in a moment.
      </div>
    );
  }

  const { records, needs_affirmation } = query.data;
  // Withdrawal eligibility comes from the server's MEMBERSHIP-derived
  // list, not from the caller's own consent rows. Deriving it from
  // records hid the control from the families most likely to want it:
  // offline-recorded consent stores user_id NULL, pre-capture and
  // roster-imported guardians have no rows at all, player-role members
  // never hold parental rows, and a co-guardian is never prompted so
  // never accumulates one.
  const withdrawableTeams = (query.data.withdrawable_teams ?? []).map((team) => ({
    team_id: team.team_id,
    team_name: team.team_name,
    players: team.players.map((p) => ({ id: p.id, name: p.display_name })),
  }));

  return (
    <div data-testid="me-consent-tab" className="flex flex-col gap-4">
      <header>
        <h2 className="flex items-center gap-2 text-base font-display uppercase tracking-wider text-ink">
          <FileCheck2 size={16} strokeWidth={2.2} aria-hidden="true" />
          Consent
        </h2>
        <p className="mt-1 text-sm text-ink-muted">
          What you've agreed to during the beta — each affirmation with its
          date and document version. Only your own affirmations appear here.
        </p>
      </header>

      {needs_affirmation.map((group) => (
        <ReaffirmPanel key={group.team_id} group={group} />
      ))}

      {records.length === 0 && needs_affirmation.length === 0 ? (
        <p className="rounded border border-rule bg-canvas p-3 text-sm text-ink-muted">
          No consent records yet. When you accept a team invite or affirm
          the beta documents, your record appears here.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {records.map((record) => (
            <ConsentRecordRow key={record.id} record={record} />
          ))}
        </ul>
      )}

      {withdrawableTeams.map((team) => (
        <WithdrawSection key={team.team_id} team={team} />
      ))}
    </div>
  );
}

function ConsentRecordRow({ record }: { record: MyConsentRecord }) {
  const isParental = record.document === "parental-consent";
  const title = isParental
    ? `${DOC_LABELS[record.document]} — ${record.player_name ?? "your player"}`
    : DOC_LABELS[record.document] ?? "Beta document";
  return (
    <li
      data-testid="me-consent-record"
      data-document={record.document}
      className="rounded border border-rule bg-surface p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-ink">{title}</span>
        <span
          data-current={record.is_current}
          className="rounded-full border border-rule px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-ink-muted data-[current=true]:border-good/40 data-[current=true]:text-good"
        >
          {record.is_current ? "Current" : "Superseded"}
        </span>
      </div>
      <p className="mt-1 text-xs text-ink-muted">
        {record.team_name} · agreed {formatDate(record.created_at)} · version{" "}
        <span className="font-mono">{record.version}</span>
      </p>
      <p className="mt-0.5 text-xs text-ink-muted">
        {record.source === "email"
          ? "Recorded by the club from your written confirmation."
          : "Affirmed by you in the app."}
      </p>
      {isParental ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {Object.entries(ITEM_LABELS).map(([key, label]) => (
            <span
              key={key}
              data-testid={`me-consent-item-${key}`}
              data-affirmed={record.items[key] === true}
              className="rounded-full border border-rule px-2 py-0.5 text-[0.65rem] text-ink-muted data-[affirmed=true]:border-good/40 data-[affirmed=true]:text-good data-[affirmed=false]:opacity-70"
            >
              {label}: {record.items[key] === true ? "agreed" : "not agreed"}
            </span>
          ))}
        </div>
      ) : null}
    </li>
  );
}

function ReaffirmPanel({ group }: { group: PendingConsentAffirmation }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [consents, setConsents] = useState<InviteConsents>(EMPTY_CONSENTS);
  const playerCount = group.role === "guardian" ? group.players.length : 0;
  const ready = requiredConsentsMet(consents, playerCount);

  const mut = useMutation({
    mutationFn: () =>
      reaffirmMyConsents(
        group.team_id,
        consents,
        group.players.map((p) => p.id),
      ),
    onSuccess: async () => {
      toast.success("Consent confirmed", {
        description: `Thanks — your consent for ${group.team_name} is up to date.`,
      });
      await queryClient.invalidateQueries({ queryKey: unscopedKey("me-consents") });
    },
    onError: (err) =>
      toast.error("Couldn't record consent", {
        description:
          err instanceof ApiError && err.status === 409
            ? err.message
            : "Please try again, or contact the club.",
      }),
  });

  return (
    <section
      data-testid="me-consent-reaffirm"
      data-status={group.status}
      data-team={group.team_id}
      className="flex flex-col gap-3 rounded border border-accent/40 bg-accent/5 p-3"
    >
      <div>
        <h3 className="text-sm font-medium text-ink">
          {group.status === "stale"
            ? `The beta documents were updated — please re-confirm for ${group.team_name}.`
            : `Consent is needed for ${group.team_name}.`}
        </h3>
        <p className="mt-1 text-xs text-ink-muted">
          {playerCount > 0
            ? `This covers ${group.players.map((p) => p.display_name).join(", ")}.`
            : "This covers your own participation."}
        </p>
      </div>
      <ConsentPanel
        playerCount={playerCount}
        consents={consents}
        onChange={setConsents}
      />
      <div>
        <Button
          type="button"
          data-testid="me-consent-reaffirm-submit"
          disabled={!ready || mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? "Recording…" : "Confirm consent"}
        </Button>
      </div>
    </section>
  );
}

function WithdrawSection({
  team,
}: {
  team: { team_id: string; team_name: string; players: { id: string; name: string }[] };
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [playerId, setPlayerId] = useState<string>("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const mut = useMutation({
    mutationFn: () =>
      createConsentWithdrawal({
        team_id: team.team_id,
        player_id: playerId || null,
        message,
      }),
    onSuccess: (result) => {
      setSubmitted(true);
      setOpen(false);
      if (result.already_pending) {
        toast.success("Request already on file", {
          description: "The club has your earlier withdrawal request.",
        });
      }
    },
    onError: () =>
      toast.error("Couldn't send the request", {
        description: "Please try again, or contact the club directly.",
      }),
  });

  return (
    <section className="rounded border border-rule bg-canvas p-3">
      <h3 className="text-sm font-medium text-ink">
        Withdrawing consent — {team.team_name}
      </h3>
      <p className="mt-1 text-xs leading-5 text-ink-muted">
        You can withdraw consent at any time. This sends a request to the
        club: your child's profile and feedback are removed, and match
        video is deleted in full on request (one player can't be edited
        out of group footage). Nothing is deleted automatically.
      </p>
      {submitted ? (
        <p
          data-testid="consent-withdraw-confirmation"
          className="mt-2 rounded border border-good/40 bg-good/5 p-2 text-xs text-ink"
        >
          Your withdrawal request has been recorded. The club will contact
          you to confirm what happens next — nothing has been deleted yet.
        </p>
      ) : open ? (
        <div
          data-testid="consent-withdraw-dialog"
          className="mt-2 flex flex-col gap-2 rounded border border-rule bg-surface p-3"
        >
          <p className="text-xs text-ink-muted">
            This records a request for the club to action — it does not
            delete anything by itself.
          </p>
          {team.players.length > 1 ? (
            <TkticSelect
              ariaLabel="What to withdraw"
              value={playerId}
              onChange={setPlayerId}
              options={[
                { value: "", label: `Everything for ${team.team_name}` },
                ...team.players.map((p) => ({
                  value: p.id,
                  label: `Only ${p.name}`,
                })),
              ]}
            />
          ) : null}
          <textarea
            data-testid="consent-withdraw-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Anything the club should know (optional)"
            className="w-full rounded border border-rule bg-canvas p-2 text-sm text-ink placeholder:text-ink-muted"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="flag"
              size="sm"
              data-testid="consent-withdraw-submit"
              disabled={mut.isPending}
              onClick={() => mut.mutate()}
            >
              {mut.isPending ? "Sending…" : "Send withdrawal request"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="me-consent-withdraw"
            onClick={() => setOpen(true)}
          >
            Request withdrawal…
          </Button>
        </div>
      )}
    </section>
  );
}
